import { isPublicStorageMount } from '../storageFileAudit/formatters';
import { parseItemPathForRoutes, type MountRoute } from './createFileBrowserProvider';

export type DirectoryWriteAccess = {
  canWrite: boolean;
  isPublicReadOnly: boolean;
  isUnavailable: boolean;
};

export function isMountUnavailable(mount: Pick<MountRoute, 'kind' | 'health'> | null | undefined): boolean {
  if (mount?.kind !== 'OneDrive') return false;
  const health = mount.health?.trim().toLowerCase();
  return health === 'auth_required' || health === 'unavailable';
}

export function mountHealthMessage(
  mount: Pick<MountRoute, 'kind' | 'health' | 'healthReason'> | null | undefined
): string | null {
  if (!isMountUnavailable(mount)) return null;
  const reason = mount?.healthReason?.trim().toLowerCase();
  if (mount?.health?.trim().toLowerCase() === 'auth_required') {
    return reason === 'graph_forbidden'
      ? 'OneDrive needs permission. Sign out and back in to approve access.'
      : 'OneDrive sign-in is required. Sign out and back in, then try again.';
  }
  if (reason === 'disabled') return 'OneDrive is temporarily disabled.';
  return 'OneDrive is temporarily unavailable. Other storage locations are still available.';
}

/** Resolves write access from the FileManager's current directory path (avoids stale React canWrite). */
export function resolveDirectoryWriteAccess(
  currentPath: string,
  routes: readonly MountRoute[]
): DirectoryWriteAccess {
  const parsedDir = routes.find((route) => currentPath === route.key || currentPath.startsWith(`${route.key}/`));
  const isUnavailable = isMountUnavailable(parsedDir);
  const canWrite = parsedDir?.mode === 'ReadWrite' && !isUnavailable;
  const isPublicReadOnly = Boolean(parsedDir && isPublicStorageMount(parsedDir.id) && !canWrite);
  return { canWrite, isPublicReadOnly, isUnavailable };
}

function isPublicReadOnlyMount(mountId: string, mode: MountRoute['mode']): boolean {
  return isPublicStorageMount(mountId) && mode !== 'ReadWrite';
}

/**
 * Public read-only when the current folder is under Public, or any selected item is on Public,
 * and that mount is not ReadWrite (non–storage-admin).
 */
export function resolveSelectionWriteAccess(
  currentPath: string,
  selected: readonly { path?: string; key?: string; name: string; dataItem?: unknown }[],
  routes: readonly MountRoute[]
): DirectoryWriteAccess {
  const fromDir = resolveDirectoryWriteAccess(currentPath, routes);
  let isPublicReadOnly = fromDir.isPublicReadOnly;

  for (const item of selected) {
    const parsed = parseItemPathForRoutes(item, routes);
    if (!parsed) continue;
    if (isPublicReadOnlyMount(parsed.mount.id, parsed.mount.mode)) {
      isPublicReadOnly = true;
      break;
    }
  }

  // At Files root, directory write is false; when selecting a writable mount keep canWrite false
  // for the root itself. When inside a mount, use directory write unless Public read-only forces it off.
  const canWrite = isPublicReadOnly ? false : fromDir.canWrite;
  const isUnavailable =
    fromDir.isUnavailable ||
    selected.some((item) => isMountUnavailable(parseItemPathForRoutes(item, routes)?.mount));
  return { canWrite: canWrite && !isUnavailable, isPublicReadOnly, isUnavailable };
}
